import { Button, Card, CardContent, DialogContent, makeStyles, TextField, Typography } from '@material-ui/core';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import * as Yup from 'yup';

import { FormBuilder } from '@zennya/web-component-library/src/components/FormBuilder';
import { useFormikContext } from 'formik';
import locationsService from 'services/locations.service';
import { useState } from 'util/stateref';
import { useTrackEvent } from 'util/trackEvent';
import { LocationAutocomplete } from './LocationAutocomplete';
import ReactJson from 'react-json-view';
import { isEqual } from 'lodash'
import { anonReverseGeocode } from 'services/anon.service';
import { toast } from 'react-hot-toast';
import { useDebounce } from 'usehooks-ts';
import { toasterror } from 'util/toasterror';

const autocompleteService = { current: null };

const useStyles = makeStyles((theme) => {
  return {
    dialogForm:{
      height: '100%',
      overflow: 'auto',
    },
    locationRoot: {
      padding: theme.spacing(1),
      "&>*":{
        marginBottom: theme.spacing(1)
      }
    },
    places: {
      paddingBottom: theme.spacing(2)
    },
    action: {
      display: "flex",
      width: "100%",
      justifyContent: "space-between",
      alignItems: "center",
      alignContent: "center",
    },
    highlight: {
      fontWeight: 'bold'
    },
    actionDataContainer: {
      display: "flex",
      flexDirection: "column",
      alignContent: "flex-start",
      alignItems: "flex-end",
    },
    dialogContent: {
      minHeight: '50vh',
      [theme.breakpoints.down("xs")]: {

        overflowY: 'scroll',
        flex: '1.5 1 auto'
      }
    },
  }
})

const FormConsumer = ({ onChange = () => { }, onDirty = () => { } }) => {
  const formikObject = useFormikContext();
  const { values, initialValues, setValues, setFieldValue, ...formik } =
    formikObject;
  useEffect(
    () => {
      const initV = (({ name, notes, type_name, guest_name, tower_name, number, street_name }) => ({ name, notes, type_name, guest_name, tower_name, number, street_name }))(initialValues)
      const V = (({ name, notes, type_name, guest_name, tower_name, number, street_name }) => ({ name, notes, type_name, guest_name, tower_name, number, street_name }))(values)
      const isEq = isEqual(V, initV)
      onDirty(!isEq)
    }, [values, initialValues]
  );
  React.useEffect(() => {
    const dirty = formik.dirty;
    onChange(values);
  }, [formik.dirty, values, formik, onChange]);
  return null;
};

const LocationForm = forwardRef(({
  sessionToken,
  altLayout = false,
  booking_id,
  mapDirty,
  currentLocation: cLoc,
  simplified,
  onSubmitCallback,
  actionTitle = "New Location",
  actionData = "",
  affirmLabel = "Add location",
  onAddLocation = () => { }, onSetMarker = () => { },
  clearOnReset = true,
  onSetNewLocation = () => { }, onSetSelectedLocation = () => { },
  cancelCallback,
  markerLocation,
  locationObject
}, ref) => {


  const classes = useStyles()

  const [address, setAddress] = React.useState("");
  const [addressComponents, setaddressComponents] = useState();

  const [value, setValue] = React.useState(null);
  const [options, setOptions] = React.useState([]);

  const [newLocation, setNewLocation] = useState(false);

  const [locType, setLocType] = useState('home');
  const [formValues, setFormValues] = useState(locationObject);

  const [currentLocation, setCurrentLocation, currentLocationRef] = useState();

  const trackEvent = useTrackEvent()

  const [isDirty, setIsDirty] = useState(false);
  const [isMapDirty, setIsMapDirty] = useState(false);

  useEffect(() => {
    
    console.log('index.jsx (114) # locationObject', locationObject);
    // setFormValues(locationObject);
  }, [locationObject]);

  useEffect(() => {
    setCurrentLocation(cLoc)
  }, [cLoc]);

  useEffect(
    () => {
      setIsMapDirty(mapDirty)
    }, [mapDirty, setIsDirty]
  );

  const handleSelect = useCallback(
    async (value) => {
      try {
        if (!value) return;  
        let geoResult = null 
        if (sessionToken) {
          geoResult = await anonReverseGeocode({
            latitude: value?.latitude,
            longitude: value?.longitude,
            session_token: sessionToken
          })
        }
        else {
          geoResult = await locationsService.reverseGeocode({ latitude: value?.latitude, longitude: value?.longitude })
        }
        const latLng = { lat: value?.latitude, lng: value?.longitude };
        setAddress(value);
        const streetNumber = ""
        const streetName = geoResult?.street ?? ""
        const city = geoResult?.city ?? ""
        const area = ""
        setFormValues({ number: streetNumber, street_name: streetName, city, area })
        setaddressComponents({ formatted_address: geoResult?.address, latLng })
        // setCurrentLocation({ latitude: latLng.lat, longitude: latLng.lng })
        onSetMarker({ latitude: latLng.lat, longitude: latLng.lng })
      } catch (error) {
        toasterror(error?.message?.en || error?.response?.data?.message?.en || error?.response?.data?.details, {error})        
      }
    },
    [onSetMarker],
  );

  const [locObject, setLocObject] = useState(null);
  const debouncedLocObject = useDebounce(locObject, 500);


  useEffect(() => {
    if (currentLocationRef?.current?.lat && currentLocationRef?.current?.lng) {
      setLocObject({ latitude: currentLocationRef?.current?.lat, longitude: currentLocationRef?.current?.lng })
    }
  }, [currentLocationRef?.current?.lat, currentLocationRef?.current?.lng]);

  useEffect(
    () => {
      handleSelect(debouncedLocObject)
    }, [debouncedLocObject]
  );

  const clearAddress = useCallback(
    () => {
      setValue()
      setOptions([])
      onAddLocation()
      onSetSelectedLocation(null)
      onSetMarker(null)
      setaddressComponents()
      setAddress()
      setFormValues()
    },
    [onAddLocation, onSetMarker, onSetSelectedLocation],
  );

  const addAddressAsCallback = useCallback(
    async (v, { dirty }) => {
      const mappedValues = {
        ...v,
        dirty: isMapDirty,
        label: v?.name,
        latitude: currentLocationRef?.current?.latitude || cLoc?.lat,
        longitude: currentLocationRef?.current?.longitude || cLoc?.lng,
        address: addressComponents?.formatted_address || v?.address
      }

      onSubmitCallback(mappedValues)
    },
    [cLoc, addressComponents?.formatted_address, clearAddress, currentLocationRef, onSetNewLocation, onSubmitCallback],
  );
  const addAddressAsLocation = useCallback(
    async (values) => {
      const mappedValues = {
        ...values,
        label: values?.name,
        latitude: currentLocationRef?.current?.latitude,
        longitude: currentLocationRef?.current?.longitude,
        address: addressComponents?.formatted_address,
      }

      //Todo: axios

    },
    [addressComponents?.formatted_address, currentLocationRef],
  );



  const formSetup = useMemo(() => {
    return {
      ...simplified ? {} : {
        name: {
          type: "text",
          label: "Location Name",
          initialValues: locationObject?.name,
          fieldProps: {
            size: "small"
          },
          validator: () => Yup.string().required().nullable().label(" "),
        },
        notes: {
          type: "text",
          label: "Notes",
          initialValues: locationObject?.notes,
          fieldProps: {
            size: "small"
          },
          validator: () => Yup.string().nullable().label(" "),
        },
      },
      type_name: {
        type: 'select',
        label: 'Type',
        initialValues: locationObject?.type_name || 'home',
        options: [
          { value: 'home', label: "House" },
          { value: 'condo', label: "Condo" },
          { value: 'hotel', label: "Hotel" },
          { value: 'office', label: "Office" },
        ],
        settings: {
          default: 'home',
          multiple: false,
          labelField: "label",
          valueField: "value",
        },
        fieldProps: {
          size: "small"
        },
        validator: () => Yup.string().required(),
      },
      guest_name: {
        type: "text",
        label: "Guest Name",
        initialValues: locationObject?.guest_name,
        fieldProps: {
          size: "small"
        },
        validator: () => Yup.string().nullable().label(" "),
      },
      tower_name: {
        type: "text",
        label: "Tower name",
        initialValues: locationObject?.tower_name,
        fieldProps: {
          size: "small"
        },
        validator: () => Yup.string().nullable().label(" "),
      },
      number: {
        type: "text",
        label: "Building/Unit No",
        initialValues: locationObject?.number,
        fieldProps: {
          size: "small"
        },
        validator: () => Yup.string().required().nullable().label(" "),
      },
      street_name: {
        type: "text",
        label: "Street Name",
        initialValues: locationObject?.street_name,
        fieldProps: {
          size: "small"
        },
        validator: () => Yup.string().required().nullable().label(" "),
      },

    }
  }, [simplified, locationObject]);

  const formLayout = useMemo(() => {
    const isHotel = locType === 'hotel' ? ['guest_name'] : []
    const isNotHome = ['office', 'condo', 'hotel'].includes(locType) ? ['tower_name'] : []
    return [
      ['name', 'type_name'],
      ['number', 'street_name'],
      ...isNotHome,
      ...isHotel,
      'notes',
    ]
  }, [locType]);

  // if (!newLocation) {
  //   return (
  //     <CardContent component={List}>
  //       <ListItem className={classes.actionDataContainer}>
  //         {actionData}
  //         <Button onClick={() => {
  //           setNewLocation(true)
  //           onSetNewLocation(true)
  //         }} variant="contained">{actionTitle}</Button>
  //       </ListItem>
  //     </CardContent>
  //   )
  // }

  const [triggerSubmit, setTriggerSubmit] = useState(false);


  useImperativeHandle(ref, () => ({
    isSubmitDisabled: () => {
      return !(isDirty || isMapDirty)
    },
    submitForm: () => {
      setTriggerSubmit(true)
    }
  }))




  const content = (
    <>
    <div className={classes.locationRoot}>
      <Typography component="p" variant='caption' color='textSecondary'>Current Address</Typography>
      <Typography component="p" variant="body2" color="textSecondary">
        {addressComponents?.formatted_address || formValues?.address}
      </Typography>
    </div>

      <LocationAutocomplete
        sessionToken={sessionToken}
        centerLocation={currentLocationRef?.current}
        onSelectAddress={loc => {
          handleSelect(loc)
        }}
      />
      <FormBuilder
        formLabel=""
        formId="newAddressForm"
        variant="outlined"
        columns={3}
        setValues={formValues}
        submitLabel={affirmLabel}
        resetLabel="Cancel"
        formFactor="default"
        formLayout={formLayout}
        form={formSetup}
        disableSubmit={!(isDirty || isMapDirty)}
        decouple={altLayout}
        triggerSubmit={triggerSubmit}
        onTriggerSubmit={v => {
          setTriggerSubmit(false)
        }}
        onSubmit={v => {
          if (onSubmitCallback) {
            addAddressAsCallback(v, { dirty: (isDirty || isMapDirty) })
          } else {
            addAddressAsLocation(v, { dirty: (isDirty || isMapDirty) })
          }
        }}
        {...cancelCallback ? {
          hasReset: false,
          additionalActions: () => (
            // <Button variant="outlined" onClick={() => cancelCallback()}>Back</Button>
            <></>
          )
        } : {
          hasReset: true,
        }}
        onReset={() => {
          if (clearOnReset) {
            clearAddress()
          }
          setNewLocation(false)
          onSetNewLocation(false)
        }}
      >
        <FormConsumer onChange={(values) => {
          setLocType(values?.type_name)
        }}
          onDirty={(it) => {
            setIsDirty(it)
          }}
        />
        {/* <ReactJson src={{ isDirty, isMapDirty }} /> */}
      </FormBuilder>
    </>
  )

  if (altLayout) {
    return <Card className={classes.dialogContent}>
      <CardContent className={classes.dialogForm} component={DialogContent}>
        {content}
      </CardContent>
    </Card>
  }
  return (
    <CardContent component={DialogContent} className={classes.dialogContent}>
      {content}
    </CardContent>
  )
})

export { LocationForm };
